<script setup>
  import { ref, computed, watch, nextTick } from "vue";
  import { ArrowRight, ChevronDown, ChevronUp, Mic, Star, User, Video } from "lucide-vue-next";
  import moment from "moment";
  import { marked } from "marked";
  import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { Textarea } from "@/components/ui/textarea";
  import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
  import { highlightKeywords, removeHtmlMarkdownAndLineBreak } from "@utils/toolBox.js";
  import { renderMarkdown, renderMarkdownInline } from "@utils/renderMarkdown.js";
  import { storeToRefs } from "pinia";
  import { useCurrentInterviewStore } from "@stores/survey/grids/currentInterview";
  import { useCurrentUserStore } from "@stores/currentUser.js";
  import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
  import MediaPlayer from "./MediaPlayer.vue";
  import { useInterviewValues } from "./useInterviewValues";

  const currentInterviewStore = useCurrentInterviewStore();
  const { personalNotes } = storeToRefs(currentInterviewStore);

  const surveyLocalesStore = useSurveyLocalesStore();
  const { currentLocale } = storeToRefs(surveyLocalesStore);
  const { isAdmin } = storeToRefs(useCurrentUserStore());
  const { columnValues } = useInterviewValues();

  const props = defineProps({
    interview: {
      type: Object,
      default: null,
    },
    scrollToMessageId: {
      type: [String, Number],
      default: null,
    },
    // [{ index, title }] — focus groups of the AI chat card, used to label highlight groups.
    objectives: {
      type: Array,
      default: () => [],
    },
    // The recode columns shown in the table: they make the profile rows of the summary.
    columns: {
      type: Array,
      default: () => [],
    },
    hasPrevious: {
      type: Boolean,
      default: false,
    },
    hasNext: {
      type: Boolean,
      default: false,
    },
    // { prompt, cell, population? } — an AI question answer opens the drawer on its own tab.
    answer: {
      type: Object,
      default: null,
    },
  });

  const MEDIA_FORMATS = ['video', 'audio'];

  const isOpen = defineModel("open", { type: Boolean, default: false });

  const emit = defineEmits(["refresh-media", "toggle-favorite", "previous", "next", "open-citation"]);

  const isPopulationAnswer = computed(() => !!props.answer?.cell?.search_group_id);

  const citedRespondents = computed(() =>
    (props.answer?.cell?.citations ?? []).filter(
      (citation, index, citations) =>
        citation.respondent_id && citations.findIndex((other) => other.respondent_id === citation.respondent_id) === index,
    ),
  );

  const tabs = computed(() =>
    [props.answer && "answer", !isPopulationAnswer.value && "summary", "conversation"].filter(Boolean),
  );

  const defaultTab = () => (isPopulationAnswer.value ? "answer" : "summary");

  const meta = computed(() => {
    if (!props.interview) return [];
    const { created_at: createdAt, completed_at: completedAt, language, id } = props.interview;
    const minutes = createdAt && completedAt ? Math.round(moment(completedAt).diff(moment(createdAt), "minutes")) : null;
    return [
      createdAt && moment(createdAt).format("MMM D, YYYY"),
      language?.toUpperCase(),
      minutes != null && minutes > 0 && `${minutes} min`,
      isAdmin.value && `ID ${id}`,
    ].filter(Boolean);
  });

  const profileRows = computed(() =>
    props.columns
      .map((column) => ({ label: column.question_name, values: columnValues(props.interview, column) }))
      .filter((row) => row.values.length),
  );

  marked.setOptions({
    breaks: true,
    gfm: true,
  });

  function parseMarkdown(content) {
    if (!content) return "";
    return marked.parse(content);
  }

  function getMessageContent(message, isMultiple) {
    const showTranslation =
      props.interview?.language !== currentLocale.value?.language && message.translated_content?.[0];
    const source = showTranslation ? message.translated_content : [message.content].flat();
    return isMultiple ? source.join(", ") : source[0];
  }

  const objectivesByIndex = computed(() => {
    const map = {};
    props.objectives.forEach((objective) => {
      map[objective.index] = objective.title;
    });
    return map;
  });

  // Group structured highlights by focus_index ascending; 0/null means "no specific objective".
  const groupedHighlights = computed(() => {
    const highlights = props.interview?.highlights || [];
    const groups = new Map();
    highlights.forEach((highlight) => {
      const key = highlight.focus_index || 0;
      if (!groups.has(key)) {
        groups.set(key, { focusIndex: key, title: objectivesByIndex.value[key] || "", items: [] });
      }
      groups.get(key).items.push(highlight);
    });
    return [...groups.values()].sort((a, b) => a.focusIndex - b.focusIndex);
  });

  const hasMediaMessages = computed(() =>
    props.interview?.conversation?.some((m) => isMediaMessage(m)));

  function isMediaMessage(message) {
    return MEDIA_FORMATS.includes(message.response_format);
  }

  const activeMediaMessageId = ref(null);
  const mediaPlayerRef = ref(null);

  const withTimeline = (entries) => {
    let cursorSeconds = 0;
    return entries.map((entry) => {
      const item = { ...entry, start: cursorSeconds, duration: entry.duration || 0 };
      cursorSeconds += entry.duration || 0;
      return item;
    });
  };

  const manifest = ref(null);

  watch(
    () => props.interview?.id,
    async (id) => {
      manifest.value = null;
      if (!props.interview?.playlist_manifest_url) return;
      try {
        const res = await fetch(props.interview.playlist_manifest_url);
        if (id !== props.interview?.id) return;
        if (res.ok) manifest.value = (await res.json()).media;
      } catch {
        manifest.value = null;
      }
    },
    { immediate: true },
  );

  const unavailableMediaIds = computed(
    () => new Set((manifest.value ?? []).filter((v) => !v.available).map((v) => v.message_id)),
  );

  const isMediaUnavailable = (message) =>
    isMediaMessage(message) && unavailableMediaIds.value.has(message.id);

  const mediaItems = computed(() =>
    withTimeline(
      manifest.value
        ? manifest.value.filter((v) => v.available).map((v) => ({ id: v.message_id, duration: v.duration, type: v.response_format }))
        : props.interview.conversation
            .filter(isMediaMessage)
            .map((m) => ({ id: m.id, duration: m.media_duration, type: m.response_format })),
    ));

  const playlistUrlForPlayer = computed(() => props.interview?.playlist_url ?? null);

  function onMediaChange(mediaId) {
    activeMediaMessageId.value = mediaId;
  }

  function applyJump(mediaId, retries = 5) {
    if (mediaPlayerRef.value) {
      mediaPlayerRef.value.jumpToItemId(mediaId);
      return;
    }
    if (retries > 0) {
      nextTick(() => applyJump(mediaId, retries - 1));
    }
  }

  function jumpToMessage(messageId) {
    if (!mediaItems.value.some((v) => v.id === messageId)) return;
    activeMediaMessageId.value = messageId;
    applyJump(messageId);
  }

  const activeTab = ref("summary");
  const pendingScrollMessageId = ref(null);
  const pendingScrollToTop = ref(false);

  const summaryScrollRef = ref(null);
  const conversationScrollRef = ref(null);

  function scrollWithRetry(attemptFn, retries = 5) {
    nextTick(() => {
      if (!attemptFn() && retries > 0) {
        setTimeout(() => scrollWithRetry(attemptFn, retries - 1), 100);
      }
    });
  }

  function centerMessageInConversation(messageId) {
    const container = conversationScrollRef.value;
    const el = document.getElementById(`message-${messageId}`);
    if (!container || !el) return false;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const delta = elRect.top - containerRect.top - (container.clientHeight - el.clientHeight) / 2;
    container.scrollTo({ top: container.scrollTop + delta, behavior: "smooth" });
    return true;
  }

  function isMessageFullyVisible(messageId) {
    const container = conversationScrollRef.value;
    const el = document.getElementById(`message-${messageId}`);
    if (!container || !el) return false;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    return elRect.top >= containerRect.top && elRect.bottom <= containerRect.bottom;
  }

  function scrollToMessage(messageId) {
    if (!messageId) return;
    scrollWithRetry(() => centerMessageInConversation(messageId));
  }

  function scrollAndJumpToMessage(messageId) {
    scrollToMessage(messageId);
    nextTick(() => jumpToMessage(messageId));
  }

  // Click-to-source: jump from a highlight quote to its source message in the conversation.
  function goToQuoteMessage(messageId) {
    pendingScrollMessageId.value = messageId;
    if (activeTab.value === "conversation") {
      scrollAndJumpToMessage(messageId);
    } else {
      activeTab.value = "conversation";
    }
  }

  watch(activeMediaMessageId, (newId) => {
    if (!newId || activeTab.value !== 'conversation') return;
    nextTick(() => {
      if (!isMessageFullyVisible(newId)) centerMessageInConversation(newId);
    });
  });

  watch(activeTab, (newTab) => {
    if (newTab !== "conversation") return;
    if (pendingScrollMessageId.value) {
      scrollAndJumpToMessage(pendingScrollMessageId.value);
    } else if (pendingScrollToTop.value) {
      scrollWithRetry(() => {
        const container = conversationScrollRef.value;
        if (container) {
          container.scrollTop = 0;
          return true;
        }
        return false;
      });
      pendingScrollToTop.value = false;
    }
  });

  watch(
    () => props.scrollToMessageId,
    (newMessageId) => {
      if (!newMessageId) return;
      pendingScrollMessageId.value = newMessageId;
      if (activeTab.value === "conversation") {
        scrollAndJumpToMessage(newMessageId);
      }
    },
  );

  function scrollTabsToTop() {
    scrollWithRetry(() => {
      const container = summaryScrollRef.value?.parentElement;
      if (container) {
        container.scrollTop = 0;
        return true;
      }
      return false;
    });
    pendingScrollToTop.value = true;
  }

  watch(
    () => props.interview,
    (newInterview, oldInterview) => {
      // Only reset the tab when switching to a different interview — a same-id refresh
      // (expired media URL refetch) must keep the user on the conversation/video tab.
      if (newInterview?.id !== oldInterview?.id) {
        activeTab.value = defaultTab();
        pendingScrollMessageId.value = null;
        scrollTabsToTop();
      }
    },
    { immediate: true },
  );

  watch(
    () => props.answer,
    (answer) => {
      if (!answer && activeTab.value === "answer") activeTab.value = "summary";
    },
  );

  defineExpose({
    activeTab,
  });
</script>

<template>
  <Sheet v-model:open="isOpen">
    <SheetContent
      side="right"
      class="flex w-[40vw] min-w-[420px] flex-col gap-0 overflow-hidden p-0 sm:max-w-[40vw]">
      <div class="shrink-0 border-b px-6 pt-5">
        <div class="flex items-center gap-3 pr-10">
          <SheetTitle
            v-if="!answer"
            class="heading-page flex items-center gap-2.5">
            Interview
            <Badge
              variant="secondary"
              class="text-sm font-medium">
              #{{ interview?.token || interview?.id }}
            </Badge>
          </SheetTitle>
          <div
            v-else
            class="min-w-0">
            <SheetTitle class="heading-title leading-snug">{{ answer.prompt }}</SheetTitle>
            <div class="mt-2 flex items-center gap-2 text-13">
              <template v-if="answer.population">
                <span
                  class="size-2.5 shrink-0 rounded-full"
                  :style="{ backgroundColor: answer.population.color }" />
                <span class="font-medium">{{ answer.population.name }}</span>
                <Badge
                  v-if="answer.population.respondents_count !== undefined"
                  variant="secondary"
                  class="font-normal">
                  {{ answer.population.respondents_count }} respondents
                </Badge>
              </template>
              <template v-else>
                <User class="size-3.5 text-muted-foreground" />
                <span class="font-medium text-primary">#{{ interview?.token }}</span>
              </template>
            </div>
            <p
              v-if="answer.population?.outdated"
              class="mt-1.5 text-xs text-status-warning-strong">
              Generated before this population last changed.
            </p>
          </div>
          <div
            v-if="interview"
            class="ml-auto flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              :class="interview.favorite ? 'text-status-warning' : 'text-foreground-subtle hover:text-status-warning'"
              :aria-label="interview.favorite ? 'Remove from favorites' : 'Add to favorites'"
              @click="emit('toggle-favorite', interview.id)">
              <Star
                class="size-4"
                :class="interview.favorite && 'fill-current'" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              :disabled="!hasPrevious"
              aria-label="Previous interview"
              @click="emit('previous')">
              <ChevronUp class="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              :disabled="!hasNext"
              aria-label="Next interview"
              @click="emit('next')">
              <ChevronDown class="size-4" />
            </Button>
          </div>
        </div>
        <p
          v-if="meta.length"
          class="mt-1 text-13 text-foreground-subtle">
          {{ meta.join(" · ") }}
        </p>

        <Tabs
          v-if="interview"
          v-model="activeTab"
          class="mt-4">
          <TabsList class="h-auto w-full justify-start gap-6 rounded-none bg-transparent p-0">
            <TabsTrigger
              v-for="tab in tabs"
              :key="tab"
              :value="tab"
              class="h-auto flex-none rounded-none border-0 border-b-2 border-transparent px-0 pb-2.5 text-sm capitalize shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none">
              {{ tab }}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Tabs
        v-if="interview"
        v-model="activeTab"
        class="flex min-h-0 flex-1 flex-col">
        <TabsContent
          v-if="answer"
          value="answer"
          class="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <div class="space-y-7">
            <section>
              <h3 class="heading-label mb-2">Answer</h3>
              <div
                class="prose prose-sm max-w-none text-sm leading-relaxed text-foreground"
                v-html="renderMarkdown(answer.cell?.answer)" />
            </section>
            <section v-if="answer.cell?.citations?.length">
              <h3 class="heading-label mb-2">Citations ({{ answer.cell.citations.length }})</h3>
              <div class="space-y-3">
                <blockquote
                  v-for="(citation, citationIndex) in answer.cell.citations"
                  :key="`${citation.message_id}-${citationIndex}`"
                  class="border-l-2 border-border-strong pl-4">
                  <p class="text-13 leading-relaxed text-foreground-secondary">« {{ citation.quote }} »</p>
                  <div class="mt-1 flex items-center gap-3">
                    <Badge
                      variant="secondary"
                      class="font-normal">
                      #{{ citation.respondent_id ?? interview?.token }}
                    </Badge>
                    <button
                      v-if="citation.message_id"
                      type="button"
                      class="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                      @click="emit('open-citation', citation.message_id)">
                      View in conversation
                      <ArrowRight class="size-3" />
                    </button>
                  </div>
                </blockquote>
              </div>
            </section>
          </div>
        </TabsContent>

        <TabsContent
          value="summary"
          class="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <div
            ref="summaryScrollRef"
            class="space-y-7">
            <section
              v-if="interview.profile_tags?.length"
              class="flex flex-wrap gap-1.5">
              <Badge
                v-for="tag in interview.profile_tags"
                :key="tag"
                variant="secondary"
                class="font-normal">
                {{ removeHtmlMarkdownAndLineBreak(tag) }}
              </Badge>
            </section>

            <section
              v-if="profileRows.length"
              class="space-y-2.5">
              <div
                v-for="row in profileRows"
                :key="row.label"
                class="grid grid-cols-[150px_minmax(0,1fr)] items-baseline gap-4 text-sm">
                <span class="truncate text-foreground-subtle">{{ row.label }}</span>
                <span
                  v-if="row.values.length === 1"
                  class="text-foreground">
                  {{ row.values[0] }}
                </span>
                <span
                  v-else
                  class="flex flex-wrap gap-1.5">
                  <Badge
                    v-for="value in row.values"
                    :key="value"
                    variant="secondary"
                    class="font-normal">
                    {{ value }}
                  </Badge>
                </span>
              </div>
            </section>

            <section v-if="interview.summary">
              <h3 class="heading-label mb-2">Portrait</h3>
              <p
                class="text-sm leading-relaxed text-foreground"
                v-html="highlightKeywords(interview.summary)" />
            </section>

            <section v-if="groupedHighlights.length">
              <h3 class="heading-label mb-2">Highlights</h3>
              <div class="space-y-3">
                <div
                  v-for="group in groupedHighlights"
                  :key="group.focusIndex"
                  class="overflow-hidden rounded-lg border">
                  <div class="flex items-baseline gap-2 border-b bg-muted px-4 py-2 text-13">
                    <span class="font-semibold text-primary">
                      {{ group.focusIndex >= 1 ? `Focus ${group.focusIndex}` : "General" }}
                    </span>
                    <span
                      v-if="group.title"
                      class="truncate text-foreground-secondary">
                      {{ group.title }}
                    </span>
                  </div>
                  <ul class="divide-y">
                    <li
                      v-for="(highlight, hIndex) in group.items"
                      :key="hIndex"
                      class="space-y-3 px-4 py-3.5">
                      <p
                        class="text-sm leading-relaxed"
                        v-html="highlightKeywords(highlight.text)" />
                      <blockquote
                        v-for="(quote, qIndex) in highlight.quotes ?? []"
                        :key="qIndex"
                        class="border-l-2 border-border-strong pl-4">
                        <p class="text-13 leading-relaxed text-foreground-secondary">
                          « {{ removeHtmlMarkdownAndLineBreak(quote.text) }} »
                        </p>
                        <button
                          v-if="quote.message_id"
                          type="button"
                          class="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                          @click="goToQuoteMessage(quote.message_id)">
                          View in conversation
                          <ArrowRight class="size-3" />
                        </button>
                      </blockquote>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            <template v-else>
              <section v-if="interview.key_highlights?.length">
                <h3 class="heading-label mb-2">Key highlights</h3>
                <ul class="space-y-2">
                  <li
                    v-for="(highlight, index) in interview.key_highlights"
                    :key="index"
                    class="text-sm leading-relaxed"
                    v-html="highlightKeywords(highlight)" />
                </ul>
              </section>
              <section v-if="interview.noteworthy_quotes?.length">
                <h3 class="heading-label mb-2">Noteworthy quotes</h3>
                <div class="space-y-3">
                  <blockquote
                    v-for="(quote, index) in interview.noteworthy_quotes"
                    :key="index"
                    class="border-l-2 border-border-strong pl-4 text-13 leading-relaxed text-foreground-secondary">
                    {{ removeHtmlMarkdownAndLineBreak(quote) }}
                  </blockquote>
                </div>
              </section>
            </template>

            <section>
              <h3 class="heading-label mb-2">Personal notes</h3>
              <Textarea
                v-model="personalNotes"
                rows="4"
                placeholder="Your notes on this interview" />
            </section>
          </div>
        </TabsContent>

        <TabsContent
          value="conversation"
          class="flex min-h-0 flex-1 flex-col">
          <div
            v-if="hasMediaMessages"
            class="shrink-0 px-6 pt-4">
            <MediaPlayer
              ref="mediaPlayerRef"
              :key="interview?.id"
              :items="mediaItems"
              :playlist-url="playlistUrlForPlayer"
              @media-change="onMediaChange"
              @refresh-expired="emit('refresh-media')" />
          </div>

          <div
            v-if="citedRespondents.length > 1"
            class="flex shrink-0 flex-wrap items-center gap-1.5 border-b px-6 py-3">
            <Badge
              v-for="citation in citedRespondents"
              :key="citation.respondent_id"
              :variant="citation.respondent_id === interview?.token?.slice(-4) ? 'default' : 'secondary'"
              class="cursor-pointer font-normal"
              @click="emit('open-citation', citation.message_id)">
              #{{ citation.respondent_id }}
            </Badge>
          </div>

          <div
            ref="conversationScrollRef"
            class="flex-1 overflow-y-auto px-6 py-5">
            <div
              v-if="interview.conversation?.length"
              class="space-y-3">
              <template
                v-for="(message, index) in interview.conversation"
                :key="index">
                <div
                  v-if="message.type === 'm_image'"
                  class="flex justify-start">
                  <img
                    :src="message.content"
                    alt="Focus image"
                    class="max-h-32 max-w-[50%] rounded-lg border" />
                </div>

                <div
                  v-else-if="message.type === 'm_gallery'"
                  class="flex justify-start flex-wrap gap-2">
                  <img
                    v-for="(url, imageIndex) in message.content"
                    :key="`focus-image-${index}-${imageIndex}`"
                    :src="url"
                    alt="Focus image"
                    class="max-h-32 max-w-[30%] rounded-lg border" />
                </div>

                <div
                  v-else-if="message.role === 'assistant'"
                  class="flex justify-start">
                  <div
                    class="prose prose-sm max-w-[85%] rounded-xl rounded-tl-sm bg-muted px-3.5 py-2.5 text-sm"
                    v-html="parseMarkdown(getMessageContent(message))" />
                </div>

                <div
                  v-else-if="message.role === 'user'"
                  :id="`message-${message.id}`"
                  class="flex justify-end transition-opacity duration-300"
                  :class="{
                    'cursor-pointer': isMediaMessage(message) && !isMediaUnavailable(message),
                    'opacity-60': hasMediaMessages && activeMediaMessageId && activeMediaMessageId !== message.id,
                  }"
                  @click="isMediaMessage(message) && !isMediaUnavailable(message) ? jumpToMessage(message.id) : null">
                  <div
                    class="max-w-[85%] rounded-xl rounded-tr-sm border border-episto-navy/15 bg-episto-navy/6 px-3.5 py-2.5"
                    :class="{
                      'ring-2 ring-primary':
                        message.id != null && (pendingScrollMessageId === message.id || activeMediaMessageId === message.id),
                    }">
                    <div
                      v-if="isMediaMessage(message)"
                      class="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <component
                        :is="message.response_format === 'audio' ? Mic : Video"
                        class="size-3.5"
                        :class="!isMediaUnavailable(message) && 'text-primary'" />
                      <span
                        v-if="isMediaUnavailable(message)"
                        class="italic">
                        {{ message.response_format === "audio" ? "Audio unavailable" : "Video unavailable" }}
                      </span>
                      <span
                        v-else-if="message.media_duration"
                        class="tabular-nums">
                        {{ message.media_duration }}s
                      </span>
                    </div>
                    <p
                      class="whitespace-pre-wrap text-sm"
                      v-html="renderMarkdownInline(getMessageContent(message, message.multiple))" />
                  </div>
                </div>
              </template>
            </div>
            <p
              v-else
              class="py-8 text-center text-sm text-muted-foreground">
              No conversation available.
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </SheetContent>
  </Sheet>
</template>
